import { useEffect, useRef, useState } from "react";
import { Info } from "lucide-react";
import { MASTERY_CATEGORIES } from "../service/mastery";
import { MasteryLevel } from "../types/enums";
import type { SummaryStats } from "./types";
import SummaryTooltip, { type SummaryEventHandler } from "./SummaryTooltip";

import { SUMMARY_LEVELS, summaryDisplayStats } from "./presentation";

export type SummaryBasis = "overall" | "domain" | "standard";

type Props = {
  stats: SummaryStats;
  name: string;
  level: string;
  basis: SummaryBasis;
  onEvent?: SummaryEventHandler;
};

export function SummaryRowStats(props: Props) {
  return (
    <>
      <SummaryBar {...props} />
      <SummaryMetrics {...props} />
    </>
  );
}

export function SummaryMetrics({
  stats: calculatedStats,
  name,
  level,
  onEvent,
}: Omit<Props, "basis">) {
  const stats = summaryDisplayStats(calculatedStats);
  const hover = (element: string) => () =>
    onEvent?.("hover_opened", { element, level });
  return (
    <div className="et-summary-metrics">
      <SummaryTooltip
        title={`${name}: at least proficient`}
        lines={[
          stats.hasDirectEvidence === false
            ? "Results appear once direct exit-ticket evidence is available in this scope."
            : stats.unit === "students"
              ? "Proficient or Mastered, out of students who have started."
              : "Proficient or Mastered, out of standards started.",
        ]}
        className="et-summary-percent"
        label={
          stats.percent === null
            ? MASTERY_CATEGORIES[MasteryLevel.NOT_STARTED].label
            : `${stats.percent}% at least proficient`
        }
        onOpen={hover("percent")}
      >
        <strong>
          {stats.percent === null
            ? MASTERY_CATEGORIES[MasteryLevel.NOT_STARTED].label
            : `${stats.percent}%`}
          <Info size={14} aria-hidden="true" />
        </strong>
        {stats.percent !== null && <span>at least proficient</span>}
      </SummaryTooltip>
    </div>
  );
}

export default function SummaryBar({
  stats: calculatedStats,
  name,
  level,
  basis,
  onEvent,
}: Props) {
  const stats = summaryDisplayStats(calculatedStats);
  const bar = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const hasItems = stats.total > 0;
  useEffect(() => {
    if (!bar.current) return;
    const measure = () => {
      if (!bar.current) return;
      const style = window.getComputedStyle(bar.current);
      const borders =
        (parseFloat(style.borderLeftWidth) || 0) +
        (parseFloat(style.borderRightWidth) || 0);
      setWidth(
        Math.max(0, bar.current.getBoundingClientRect().width - borders),
      );
    };
    measure();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(bar.current);
    return () => observer.disconnect();
  }, [hasItems]);

  if (!stats.total) {
    return (
      <div className="et-summary-bar et-summary-bar-empty">
        {stats.unit === "students"
          ? "No students at this level"
          : "No standards"}
      </div>
    );
  }
  return (
    <div
      ref={bar}
      className="et-summary-bar"
      role="group"
      aria-label={`${name}: ${stats.total} ${stats.unit}`}
    >
      {SUMMARY_LEVELS.filter((status) => stats.counts[status] > 0).map(
        (status) => {
          const count = stats.counts[status];
          const share = count / stats.total;
          const label = MASTERY_CATEGORIES[status].label;
          const members = stats.members?.[status];
          const lines = [
            `${label}: ${count} of ${stats.total} ${stats.unit} (${Math.round(share * 100)}%).`,
            stats.hasDirectEvidence === false
              ? "Results appear once direct exit-ticket evidence is available in this scope."
              : basis === "standard"
                ? "Each student’s color uses their full mastery score when they have direct exit-ticket evidence; otherwise N/A."
                : stats.unit === "standards"
                  ? "Each standard keeps its full mastery score when this student has direct exit-ticket evidence; otherwise N/A."
                  : `Each student’s ${basis === "domain" ? "domain" : "overall"} color averages points on enrolled-grade standards with their own direct exit-ticket evidence.`,
            ...(members?.length
              ? [
                  `${members.slice(0, 8).join(", ")}${members.length > 8 ? `, and ${members.length - 8} more` : ""}.`,
                ]
              : []),
          ];
          return (
            <SummaryTooltip
              key={status}
              title={`${name}: ${label}`}
              lines={lines}
              className={`et-summary-segment et-summary-segment-${status}${status === MasteryLevel.NOT_STARTED ? " et-standards-not-started" : ""}`}
              style={{ width: `${share * 100}%` }}
              label={`${label}: ${count} of ${stats.total} ${stats.unit}`}
              onOpen={() =>
                onEvent?.("hover_opened", {
                  element: "bar_segment",
                  level,
                  status,
                })
              }
            >
              {share * width >= (count === 1 ? 12 : 24) && (
                <span aria-hidden="true">{count}</span>
              )}
            </SummaryTooltip>
          );
        },
      )}
    </div>
  );
}
